import { useEffect, useCallback, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Pagination } from '../ui/pagination';
import {
  Download,
  Loader2,
  AlertCircle,
  ChevronDown,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import { drilldownAPI } from '../../lib/api';
import { useView } from '../../contexts/ViewContext';
import {
  OTD_TABS,
  COMMON_LINKABLE_FIELDS,
  formatDrilldownValue,
  createFieldLink,
  createOTDStatusBadge,
  downloadExportedData,
  type DrilldownField,
} from './drilldownUtils';

interface OTDDrilldownModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters?: Record<string, unknown>;
  contextLabel?: string;
  initialTab?: 'on_time' | 'early' | 'late' | 'total';
}

export default function OTDDrilldownModal({
  isOpen,
  onClose,
  filters = {},
  contextLabel,
  initialTab,
}: OTDDrilldownModalProps) {
  const { viewLevel, getViewQueryParams } = useView();

  const [activeTab, setActiveTab] = useState<string>(initialTab || 'on_time');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [records, setRecords] = useState<Array<Record<string, unknown>>>([]);
  const [fields, setFields] = useState<DrilldownField[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [pageSize, setPageSize] = useState(25);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const fetchTabCounts = useCallback(async () => {
    const counts: Record<string, number> = {};
    const viewParams = getViewQueryParams();

    for (const tab of OTD_TABS) {
      try {
        const response = await drilldownAPI.getDetails('otd_grns', {
          ...filters,
          ...tab.filter,
          view_level: viewLevel,
          ...viewParams,
          page: 1,
          page_size: 1,
        });
        counts[tab.key] = response.data.count;
      } catch {
        counts[tab.key] = 0;
      }
    }

    setTabCounts(counts);
  }, [filters, viewLevel, getViewQueryParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const activeTabConfig = OTD_TABS.find(t => t.key === activeTab);
    const tabFilter = activeTabConfig?.filter || {};
    const viewParams = getViewQueryParams();

    try {
      const response = await drilldownAPI.getDetails('otd_grns', {
        ...filters,
        ...tabFilter,
        view_level: viewLevel,
        ...viewParams,
        page: currentPage,
        page_size: pageSize,
      });

      const { results, count } = response.data;
      setRecords(results.records);
      setFields(results.fields);
      setTotalCount(count);
      setTotalPages(Math.ceil(count / pageSize));
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to load OTD data');
    } finally {
      setLoading(false);
    }
  }, [filters, activeTab, currentPage, pageSize, viewLevel, getViewQueryParams]);

  useEffect(() => {
    if (isOpen) {
      fetchTabCounts();
      fetchData();
    }
  }, [isOpen, fetchTabCounts, fetchData]);

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
    }
  }, [activeTab, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab || 'on_time');
    } else {
      setActiveTab(initialTab || 'on_time');
      setCurrentPage(1);
      setRecords([]);
    }
  }, [isOpen, initialTab]);

  const renderCellValue = (fieldKey: string, value: unknown, record: Record<string, unknown>) => {
    const formattedValue = formatDrilldownValue(value);
    const linkConfig = COMMON_LINKABLE_FIELDS[fieldKey];

    if (linkConfig && value) {
      const url = linkConfig.urlPattern(String(value), record);
      return createFieldLink(url, formattedValue);
    }

    if (fieldKey === 'otd_status') {
      return createOTDStatusBadge(formattedValue);
    }

    return formattedValue;
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    setExporting(true);
    setShowExportMenu(false);

    const activeTabConfig = OTD_TABS.find(t => t.key === activeTab);
    const tabFilter = activeTabConfig?.filter || {};
    const viewParams = getViewQueryParams();

    try {
      const response = await drilldownAPI.exportData('otd_grns', format, {
        ...filters,
        ...tabFilter,
        view_level: viewLevel,
        ...viewParams,
      });

      downloadExportedData(response.data, `otd_grns_${activeTab}_export`, format);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-7xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl">
                OTD Analysis
                {contextLabel && (
                  <span className="text-muted-foreground font-normal ml-2">
                    - {contextLabel}
                  </span>
                )}
              </DialogTitle>
              <DialogDescription>
                Drill down into On-Time Delivery performance by delivery status
              </DialogDescription>
            </div>
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={exporting || records.length === 0}
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
              {showExportMenu && (
                <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border z-50">
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Export as CSV
                  </button>
                  <button
                    onClick={() => handleExport('xlsx')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Export as Excel
                  </button>
                </div>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Tab Headers with Counts */}
        <div className="border-b">
          <div className="flex">
            {OTD_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <div className="flex flex-col items-center">
                  <span className="text-lg font-bold">
                    {tabCounts[tab.key]?.toLocaleString() ?? '-'}
                  </span>
                  <span className="text-xs mt-0.5">{tab.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-h-0 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading data...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full text-red-500">
              <AlertCircle className="h-8 w-8 mb-2" />
              <p>{error}</p>
              <Button variant="outline" size="sm" onClick={fetchData} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : records.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <p>No records found for this filter</p>
            </div>
          ) : (
            <div>
              <table className="w-full">
                <thead className="bg-muted/50 sticky top-0 z-10">
                  <tr>
                    {fields.map((field) => (
                      <th
                        key={field.field}
                        className="text-left py-3 px-4 text-sm font-medium whitespace-nowrap"
                      >
                        {field.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map((record, idx) => (
                    <tr
                      key={String(record['id'] ?? idx)}
                      className="border-b hover:bg-muted/30 transition-colors"
                    >
                      {fields.map((field) => (
                        <td
                          key={field.field}
                          className="py-3 px-4 text-sm whitespace-nowrap"
                        >
                          {renderCellValue(field.field, record[field.field], record)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && !error && totalPages > 1 && (
          <div className="flex-shrink-0 -mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsPerPage={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
              itemLabel="GRN records"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
